
import React, { useState } from 'react';
import { User, LogoConfig } from '../../types';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import ThemeToggle from '../ui/ThemeToggle';
import { useAppContext } from '../../hooks/useAppContext';
import { getResolvedLogoImageUrl } from '../../services/brandAssets';

interface PortalHeaderProps {
  currentUser: User;
  onLogout: () => void;
}

const PortalHeader: React.FC<PortalHeaderProps> = ({ currentUser, onLogout }) => {
  const { siteContent } = useAppContext();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  
  const footerInfo = siteContent['footer_info'] || { companyName: "AGM GROUP" };
  const logoConfig: LogoConfig = siteContent['logo_config'] || { type: 'image', text: 'AGM GROUP', height: 60, footerHeight: 40, imageUrl: getResolvedLogoImageUrl() };
  const logoImageUrl = getResolvedLogoImageUrl(logoConfig);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-[72px] items-center justify-between gap-3 sm:min-h-[80px]">
            <div className="flex h-full min-w-0 cursor-pointer items-center py-2 max-w-[220px] sm:max-w-[300px]" onClick={() => window.location.href = '/'}>
              {logoConfig.type === 'image' && logoImageUrl && !imgError ? (
                  <img 
                    src={logoImageUrl}
                    alt="Portal Logo" 
                    onError={() => setImgError(true)}
                    style={{ height: `${Math.min(logoConfig.height || 60, 80)}px` }}
                    className="h-auto max-h-[52px] w-auto object-contain transition-all sm:max-h-[64px]"
                  />
              ) : (
                  <h1 className="truncate font-black tracking-tighter text-indigo-600" style={{ fontSize: `${Math.min((logoConfig.height || 60) / 2.5, 32)}px` }}>
                    {logoConfig.text || footerInfo.companyName}
                  </h1>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle compact />
              <div className="hidden text-right sm:block">
                <p className="text-sm font-black text-gray-900 dark:text-slate-100">{currentUser.name}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600 dark:text-sky-300">{currentUser.role}</p>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setIsLogoutModalOpen(true)} className="border-gray-300 px-3 font-black text-gray-900 dark:text-slate-100 sm:px-4">Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <Modal isOpen={isLogoutModalOpen} onClose={() => setIsLogoutModalOpen(false)} title="Confirm Logout">
        <div className="space-y-4">
          <p className="font-medium text-gray-900 dark:text-slate-100">Are you sure you want to sign out of your account?</p>
          <div className="flex justify-end space-x-3">
            <Button variant="secondary" onClick={() => setIsLogoutModalOpen(false)} className="text-gray-900">Cancel</Button>
            <Button variant="danger" onClick={() => { setIsLogoutModalOpen(false); onLogout(); }}>Logout</Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PortalHeader;
